/**
 * PeriodHeader: one centred row for a person's pay period on My time.
 * The house glyph buttons step one period back or forward, the period
 * label sits between them, and a calendar button opens a small Go to
 * date popover whose Go (or Enter in the field) jumps straight to the
 * typed date. Typing changes only the draft; the form's submit is the
 * one path to a jump, and it refuses an empty or partly typed date
 * because an empty period_start would reach the server as junk.
 *
 * The popover is non-modal: Escape closes it and returns focus to the
 * calendar button; a mousedown outside closes it without moving focus;
 * a second click on the button closes it too. The page keeps the period
 * on screen while the next loads, so the button that asked keeps focus.
 */

import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { Field } from '@/components/primitives/Field';
import type { HoursPeriodWire } from '@/lib/timeEntriesApi';
import { DateInput } from '@/components/primitives/DateInput';

/** The earliest date the jump box accepts, matching the fallback epoch (api/_shared/hoursPeriod.ts). */
const PERIOD_JUMP_MIN = '2000-01-01';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** A complete date on or after the epoch: the only value Go sends. */
function isJumpDate(value: string): boolean {
  return ISO_DATE_RE.test(value) && value >= PERIOD_JUMP_MIN;
}

export interface PeriodHeaderProps {
  period: HoursPeriodWire;
  /** The picker's max, a hint only; a typed later date still jumps. */
  todayIso: string;
  onPrevious: () => void;
  onNext: () => void;
  /** Called with the typed YYYY-MM-DD when Go or Enter submits a valid date. */
  onGoTo: (date: string) => void;
}

export function PeriodHeader({ period, todayIso, onPrevious, onNext, onGoTo }: PeriodHeaderProps): ReactNode {
  const popoverId = useId();
  const inputId = useId();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  function openPopover(): void {
    setDraft(period.start);
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      buttonRef.current?.focus();
      setOpen(false);
    }
    function onMouseDown(e: MouseEvent): void {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  function onGo(e: FormEvent): void {
    e.preventDefault();
    if (!isJumpDate(draft)) return;
    buttonRef.current?.focus();
    setOpen(false);
    onGoTo(draft);
  }

  return (
    <div
      data-testid="period-header"
      className="flex items-center justify-center gap-0.5 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:gap-0"
    >
      <div data-testid="period-nav" className="flex min-w-0 items-center sm:col-start-2 sm:gap-1">
        <Button variant="ghost" size="sm" aria-label="Previous period" data-testid="period-prev" onClick={onPrevious}>
          ‹
        </Button>
        <div className="min-w-0 text-center">
          <h2
            data-testid="period-label"
            className="mx-0.5 font-display text-lg text-sage-800 sm:mx-2 sm:text-xl sm:whitespace-nowrap"
          >
            {period.label}
          </h2>
          {!period.open && (
            <p data-testid="period-closed" className="mt-0.5 text-xs text-ink-soft">
              Locked for payroll
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" aria-label="Next period" data-testid="period-next" onClick={onNext}>
          ›
        </Button>
      </div>
      <div className="flex items-center pl-0.5 sm:col-start-3 sm:gap-2.5 sm:pl-2.5">
        <span aria-hidden="true" className="hidden h-5 w-px bg-sage-200 sm:block" />
        <div ref={wrapperRef} className="relative">
          <Button
            ref={buttonRef}
            variant="secondary"
            size="sm"
            data-testid="period-date-button"
            icon={<Calendar size={16} aria-hidden="true" />}
            aria-label="Go to date"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? popoverId : undefined}
            className="!px-2 aria-expanded:border-sage-400 aria-expanded:bg-sage-50"
            onClick={() => (open ? setOpen(false) : openPopover())}
          />
          {open && (
            <div
              id={popoverId}
              role="dialog"
              aria-label="Go to date"
              data-testid="period-date-popover"
              className="absolute right-[-4px] top-[calc(100%+10px)] z-30 w-[16.5rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-sage-800/[8%] bg-paper px-4 pt-3.5 pb-4 shadow-sh3 sm:right-[-8px] sm:w-[17.5rem] before:absolute before:-top-1.5 before:right-[13px] sm:before:right-[19px] before:h-[11px] before:w-[11px] before:rotate-45 before:border-l before:border-t before:border-sage-800/[8%] before:bg-paper before:content-['']"
            >
              <form data-testid="period-date-form" noValidate onSubmit={onGo}>
                <Field label="Go to date" htmlFor={inputId}>
                  <div className="flex items-center gap-2.5">
                    <DateInput
                      id={inputId}
                      ref={inputRef}
                      data-testid="period-date"
                      size="field" className="w-full min-w-0 flex-1"
                      min={PERIOD_JUMP_MIN}
                      max={todayIso}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <Button type="submit" variant="primary" size="md" data-testid="period-date-go" disabled={!isJumpDate(draft)}>
                      Go
                    </Button>
                  </div>
                </Field>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
